import { create } from 'zustand';
import { HubConnectionBuilder, HttpTransportType, LogLevel, type HubConnection } from '@microsoft/signalr';

import type { PlanToIfcJob } from 'app/modules/wallforming/components/plan-to-ifc/types/planToIfcTypes';

/** Incremental feedback for a chat turn, correlated by the `X-Operation-Id` sent with the request. */
export interface CopilotActivity {
    operationId: string;
    kind: 'status' | 'reasoning' | 'answer' | 'tool';
    text: string;
}

// Server method names of `ICopilotClient` (SignalR matches them case-insensitively).
const PLAN_TO_IFC_JOB_UPDATED = 'PlanToIfcJobUpdatedAsync';
const COPILOT_ACTIVITY = 'CopilotActivityAsync';
const RECONNECT_DELAYS_MS = [0, 2_000, 5_000, 10_000, 30_000];
const RESTART_DELAY_MS = 10_000;
const IDLE_STOP_DELAY_MS = 5_000;

interface CopilotHubState {
    isConnected: boolean;
    /** Increments on every (re)connect so consumers can resynchronize state that may have changed while offline. */
    connectionVersion: number;
}

export const useCopilotHubStore = create<CopilotHubState>(() => ({ isConnected: false, connectionVersion: 0 }));

const jobListeners = new Set<(job: PlanToIfcJob) => void>();
const activityListeners = new Set<(activity: CopilotActivity) => void>();
let connection: HubConnection | null = null;
let users = 0;
let getToken: () => string | undefined = () => undefined;
let restartTimer: ReturnType<typeof setTimeout> | undefined;
let stopTimer: ReturnType<typeof setTimeout> | undefined;

const markConnected = () =>
    useCopilotHubStore.setState((state) => ({ isConnected: true, connectionVersion: state.connectionVersion + 1 }));

const markDisconnected = () => useCopilotHubStore.setState({ isConnected: false });

const start = async (target: HubConnection) => {
    clearTimeout(restartTimer);
    try {
        await target.start();
        if (connection === target) {
            markConnected();
        }
    } catch {
        // Automatic reconnect only covers established connections; initial failures are retried here.
        if (connection === target && users > 0) {
            restartTimer = setTimeout(() => void start(target), RESTART_DELAY_MS);
        }
    }
};

const createConnection = () => {
    const created = new HubConnectionBuilder()
        .withUrl(`${import.meta.env.VITE_APP_API_URL}copilotHub`, {
            accessTokenFactory: () => getToken() ?? '',
            transport: HttpTransportType.WebSockets | HttpTransportType.ServerSentEvents | HttpTransportType.LongPolling
        })
        .withAutomaticReconnect(RECONNECT_DELAYS_MS)
        .configureLogging(LogLevel.Warning)
        .build();
    created.on(PLAN_TO_IFC_JOB_UPDATED, (job: PlanToIfcJob) => jobListeners.forEach((listener) => listener(job)));
    created.on(COPILOT_ACTIVITY, (activity: CopilotActivity) =>
        activityListeners.forEach((listener) => listener(activity))
    );
    created.onreconnecting(markDisconnected);
    created.onreconnected(markConnected);
    created.onclose(() => {
        markDisconnected();
        if (connection === created && users > 0) {
            restartTimer = setTimeout(() => void start(created), RESTART_DELAY_MS);
        }
    });
    return created;
};

/** Shares one connection between all consumers; it is stopped shortly after the last one releases it. */
export const acquireCopilotHub = (getAccessToken: () => string | undefined) => {
    getToken = getAccessToken;
    users++;
    clearTimeout(stopTimer);
    if (!connection) {
        connection = createConnection();
        void start(connection);
    }

    let released = false;
    return () => {
        if (released) {
            return;
        }
        released = true;
        users--;
        if (users > 0) {
            return;
        }
        stopTimer = setTimeout(() => {
            if (users === 0 && connection) {
                const stopping = connection;
                connection = null;
                clearTimeout(restartTimer);
                markDisconnected();
                void stopping.stop();
            }
        }, IDLE_STOP_DELAY_MS);
    };
};

export const subscribeToPlanToIfcJobs = (listener: (job: PlanToIfcJob) => void) => {
    jobListeners.add(listener);
    return () => {
        jobListeners.delete(listener);
    };
};

export const subscribeToCopilotActivity = (listener: (activity: CopilotActivity) => void) => {
    activityListeners.add(listener);
    return () => {
        activityListeners.delete(listener);
    };
};
